/**
 * state.js - Persistence with localStorage/sessionStorage
 *
 * Allows form inputs to persist their values across page loads.
 * Useful for form drafts, preferences, etc.
 */

// Storage key prefix to avoid collisions
const PREFIX = 'htmlstar:';

// Track initialized elements
const INIT_KEY = '_htmlstarPersist';

/**
 * Get the appropriate storage based on scope
 *
 * @param {HTMLElement} el - Element to check scope for
 * @returns {Storage}
 */
function getStorage(el) {
  const scope = el.dataset.persistScope;
  return scope === 'session' ? sessionStorage : localStorage;
}

/**
 * Get the storage key for an element
 *
 * @param {HTMLElement} el - Element with data-persist
 * @returns {string}
 */
function getKey(el) {
  return PREFIX + el.dataset.persist;
}

/**
 * Save an element's value to storage
 *
 * @param {HTMLElement} el - Input element
 */
function saveValue(el) {
  const storage = getStorage(el);
  const key = getKey(el);

  if (el.type === 'checkbox') {
    storage.setItem(key, el.checked ? 'true' : 'false');
  } else if (el.type === 'radio') {
    if (el.checked) {
      storage.setItem(key, el.value);
    }
  } else {
    storage.setItem(key, el.value);
  }
}

/**
 * Restore an element's value from storage
 *
 * @param {HTMLElement} el - Input element
 */
function restoreValue(el) {
  const storage = getStorage(el);
  const key = getKey(el);
  const saved = storage.getItem(key);

  if (saved === null) return;

  if (el.type === 'checkbox') {
    el.checked = saved === 'true';
  } else if (el.type === 'radio') {
    el.checked = el.value === saved;
  } else {
    el.value = saved;
  }
}

/**
 * Clear persisted value for an element
 *
 * @param {HTMLElement} el - Input element
 */
export function clearPersisted(el) {
  const storage = getStorage(el);
  const key = getKey(el);
  storage.removeItem(key);
}

/**
 * Clear all persisted values matching a prefix
 *
 * @param {string} prefix - Key prefix (without htmlstar:)
 * @param {string} scope - 'local' or 'session'
 */
export function clearPersistedByPrefix(prefix, scope = 'local') {
  const storage = scope === 'session' ? sessionStorage : localStorage;
  const fullPrefix = PREFIX + prefix;

  const keysToRemove = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key.startsWith(fullPrefix)) {
      keysToRemove.push(key);
    }
  }

  for (const key of keysToRemove) {
    storage.removeItem(key);
  }
}

/**
 * Initialize persistence for elements with data-persist
 *
 * @param {HTMLElement|Document} root - Root element to search within
 */
export function initPersistence(root) {
  const elements = root.querySelectorAll('[data-persist]');

  for (const el of elements) {
    if (el[INIT_KEY]) continue;
    el[INIT_KEY] = true;

    // Restore saved value
    restoreValue(el);

    // Save on input/change
    const event = el.type === 'checkbox' || el.type === 'radio' ? 'change' : 'input';
    el.addEventListener(event, () => saveValue(el));
  }

  // Handle form resets - clear persisted values
  const forms = root.querySelectorAll('form');
  for (const form of forms) {
    if (form._htmlstarPersistReset) continue;
    form._htmlstarPersistReset = true;

    form.addEventListener('reset', () => {
      const persistedInputs = form.querySelectorAll('[data-persist]');
      for (const input of persistedInputs) {
        clearPersisted(input);
      }
    });
  }
}
